import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * RC-137a — FE peer of `SecurityContext.getActiveScope()` on the BE.
 *
 * <h2>The contract</h2>
 *
 * Returns the caller's currently-pinned scope tuple in a single
 * consistent read:
 *
 *     { customerId, orgId, tenantId, membershipId }
 *
 * Each field is JWT-sourced (via AuthContext.session, which itself
 * is populated from the BE's `/api/v1/auth/session` endpoint — which
 * post-RC-137a reads all four from `SecurityContext` instead of from
 * the user's membership rows). Any field can independently be null:
 *
 *   • customerId — null for legacy users without a cid claim
 *   • orgId — null when no org pinned (PA who's picked a customer
 *     but not yet a tenant)
 *   • tenantId — null when PA hasn't pinned a tenant; "" (empty
 *     string) was used by the legacy session shape — this hook
 *     normalises empty-string to null so consumers can do a single
 *     `if (scope.tenantId)` check
 *   • membershipId — null for tokens issued before V179
 *
 * <h2>Why this hook exists</h2>
 *
 * Two real failure modes the codebase has hit:
 *
 *   1. **Inconsistent reads.** Without an aggregate, callers
 *      hand-roll `{ orgId: session?.orgId, tenantId: session?.tenantId }`
 *      every time, and on the BE one endpoint (AuthController.getSession
 *      pre-RC-137a) accidentally mixed JWT-sourced tenantId with a
 *      membership-sourced orgId — returning a structurally inconsistent
 *      tuple. PA after switchTenant saw `tid=NEW, oid=sentinel` and
 *      every filter call collapsed to "0 results". The aggregate
 *      reads from one source in one place; if a future drift
 *      happens, it's visible in one file, not 40.
 *
 *   2. **Filter scope drift.** Pages reading `session?.orgId`
 *      directly bypass any future scope-picker reactivity — when
 *      the operator pins a different scope mid-session, the page
 *      doesn't re-render unless it depends on the right key. The
 *      hook makes that dependency explicit.
 *
 * <h2>Usage</h2>
 *
 *     const scope = useActiveScope();
 *     api.getAgentInventory({
 *       orgId: scope.orgId,
 *       tenantId: scope.tenantId,
 *       includeShadow: true,
 *     });
 *
 * Or spread the whole tuple when the API accepts it:
 *
 *     api.someListEndpoint({ ...scope, otherFilter: x });
 *
 * The `isUUID` guards inside FE api helpers treat null as "skip" —
 * so a null scope field cleanly omits the param. Consumers don't
 * need to add their own null checks.
 *
 * <h2>Why not just useAuth().session?</h2>
 *
 * Two reasons:
 *   1. `session` carries a lot more than scope (role, permissions,
 *      mfaVerified, etc.) — destructuring just the scope at every
 *      call site is repetitive and error-prone.
 *   2. The empty-string-to-null normalisation only happens here —
 *      direct `session.tenantId` access in a consumer would have to
 *      handle "" itself, and many forget.
 *
 * <h2>Future</h2>
 *
 * When a future explicit "scope picker chip in the top nav"
 * (separate from the user-dropdown picker) ships, the hook reads
 * from that override store first and falls back to session. Call
 * sites don't change — they keep calling `useActiveScope()`.
 */
export function useActiveScope() {
  const { session } = useAuth() || {};

  return useMemo(() => {
    // Empty-string-to-null normalisation. The legacy session shape
    // uses "" for "not set" on tenantId/orgId (matches the Java
    // `String orgId = ...` default), so we coerce here once so the
    // 80+ consumer pages don't each have to repeat the check.
    const norm = (v) => (v == null || v === '' ? null : v);

    return {
      customerId: norm(session?.customerId),
      orgId: norm(session?.orgId),
      tenantId: norm(session?.tenantId),
      // RC-Instance-Scope-Enforcement (#210, 2026-06-22) — expose the
      // JWT-pinned instance to consumers. Already on the session shape
      // (set by switchInstance + V191 auto-pin per RC-137d); just wasn't
      // surfaced through this hook. Catalog surfaces (/api/v1/forms,
      // /api/v1/workflows, etc.) now pass it to the BE so Instance-bound
      // roles (Instance Admin / Developer / Operator per §3 of
      // RBAC_REDESIGN_V2.md) get authoritative instance narrowing,
      // and chip switches for higher-tier roles trigger UX-level
      // narrowing on those surfaces that opt in.
      instanceId: norm(session?.instanceId),
      membershipId: norm(session?.membershipId),
    };
  }, [
    session?.customerId,
    session?.orgId,
    session?.tenantId,
    session?.instanceId,
    session?.membershipId,
  ]);
}

export default useActiveScope;
